import { useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Upload, FileText, CheckCircle, AlertCircle, Loader2, X, Copy, Files } from 'lucide-react';

const MAX_BATCH = 50;

export default function InvoiceUploadPage() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadMode, setUploadMode] = useState('single');
  const [progress, setProgress] = useState(0);

  const handleFiles = (fileList) => {
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const newFiles = Array.from(fileList).filter(f =>
      allowed.includes(f.type) || f.name.endsWith('.pdf')
    );
    if (uploadMode === 'single') {
      setFiles(newFiles.slice(0, 1));
    } else {
      setFiles(prev => {
        const combined = [...prev, ...newFiles];
        return combined.slice(0, MAX_BATCH);
      });
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  }, [uploadMode]);

  const uploadSingle = async () => {
    setUploading(true);
    setProgress(0);
    const uploadResults = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const form = new FormData();
        form.append('file', file);
        const { data } = await api.post('/invoices/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadResults.push({ name: file.name, success: true, id: data.invoice?.id });
      } catch (err) {
        const detail = err.response?.data?.detail || 'Upload failed';
        const isDuplicate = err.response?.status === 409;
        uploadResults.push({ name: file.name, success: false, error: detail, isDuplicate });
      }
      setProgress(Math.round(((i + 1) / files.length) * 100));
    }
    setResults(uploadResults);
    setFiles([]);
    setUploading(false);
  };

  const uploadBatch = async () => {
    setUploading(true);
    setProgress(0);
    try {
      const form = new FormData();
      files.forEach(f => form.append('files', f));
      const { data } = await api.post('/invoices/batch-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const batchResults = (data.results || []).map(r => ({
        name: r.name,
        success: r.success,
        id: r.id,
        error: r.error,
        isDuplicate: r.error?.toLowerCase().includes('duplicate'),
      }));
      setResults(batchResults);
      setProgress(100);
    } catch (err) {
      setResults([{ name: 'Batch Upload', success: false, error: err.response?.data?.detail || 'Batch upload failed' }]);
    }
    setFiles([]);
    setUploading(false);
  };

  const handleUpload = () => {
    if (uploadMode === 'batch' && files.length > 1) {
      uploadBatch();
    } else {
      uploadSingle();
    }
  };

  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;
  const dupCount = results.filter(r => r.isDuplicate).length;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="upload-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Invoice Upload</h1>
          <p className="text-sm text-slate-500 mt-1">Upload vendor invoices for AI-powered extraction</p>
        </div>
        <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-sm" data-testid="upload-mode-toggle">
          <button
            onClick={() => { setUploadMode('single'); setFiles([]); }}
            data-testid="mode-single"
            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
              uploadMode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText className="h-3 w-3 inline mr-1" />Single
          </button>
          <button
            onClick={() => { setUploadMode('batch'); setFiles([]); }}
            data-testid="mode-batch"
            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
              uploadMode === 'batch' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Files className="h-3 w-3 inline mr-1" />Batch
          </button>
        </div>
      </div>

      {/* Drop Zone */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-0">
          <div
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            data-testid="upload-dropzone"
            className={`py-32 px-8 text-center border-2 border-dashed rounded-sm transition-all cursor-pointer ${
              dragOver ? 'border-[#007AFF] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/30'
            }`}
            onClick={() => document.getElementById('file-input').click()}
          >
            <Upload className="h-10 w-10 mx-auto text-slate-300 stroke-[1.5px]" />
            <p className="mt-4 text-sm font-medium text-slate-700">
              {uploadMode === 'batch' ? 'Drop multiple files here or click to browse' : 'Drop file here or click to browse'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              PDF, JPEG, PNG, TIFF, Excel, CSV {uploadMode === 'batch' && `(max ${MAX_BATCH} files per batch)`}
            </p>
            <input
              id="file-input"
              type="file"
              multiple={uploadMode === 'batch'}
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.csv,.xlsx"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="upload-file-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Queued Files */}
      {files.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <CardTitle className="text-sm font-semibold text-slate-800">
                Queued Files ({files.length})
              </CardTitle>
              {uploadMode === 'batch' && (
                <Badge variant="outline" className="rounded-sm text-[10px] border-slate-300 text-slate-500">
                  {MAX_BATCH - files.length} slots remaining
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFiles([])}
                data-testid="clear-files-button"
                className="rounded-sm text-xs text-slate-400 hover:text-red-500"
              >
                Clear All
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading}
                data-testid="upload-submit-button"
                className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white active:scale-[0.98] transition-all"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading ? 'Uploading...' : uploadMode === 'batch' ? `Upload Batch (${files.length})` : 'Upload'}
              </Button>
            </div>
          </CardHeader>
          {uploading && (
            <div className="px-5 pb-2">
              <Progress value={progress} className="h-1.5 rounded-sm" data-testid="upload-progress" />
              <p className="text-[10px] text-slate-400 mt-1 font-mono">{progress}% complete</p>
            </div>
          )}
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3" data-testid={`queued-file-${i}`}>
                  <FileText className="h-4 w-4 text-slate-400 stroke-[1.5px]" />
                  <span className="text-sm text-slate-700 flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-slate-400 font-mono">{(f.size / 1024).toFixed(1)} KB</span>
                  <button
                    onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                    className="text-slate-300 hover:text-red-500 transition-colors"
                    data-testid={`remove-file-${i}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Results */}
      {results.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-800">Upload Results</CardTitle>
            <div className="flex items-center gap-2">
              {successCount > 0 && (
                <Badge variant="outline" className="rounded-sm text-[10px] border-green-300 text-green-700 bg-green-50">
                  {successCount} uploaded
                </Badge>
              )}
              {dupCount > 0 && (
                <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 text-amber-700 bg-amber-50" data-testid="duplicate-count-badge">
                  <Copy className="h-3 w-3 mr-1" />{dupCount} duplicate{dupCount > 1 ? 's' : ''}
                </Badge>
              )}
              {failCount > 0 && failCount !== dupCount && (
                <Badge variant="outline" className="rounded-sm text-[10px] border-red-300 text-red-700 bg-red-50">
                  {failCount - dupCount} failed
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {results.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-5 py-3" data-testid={`result-row-${i}`}>
                  {r.success ? (
                    <CheckCircle className="h-4 w-4 text-green-500 stroke-[1.5px] flex-shrink-0" />
                  ) : r.isDuplicate ? (
                    <Copy className="h-4 w-4 text-amber-500 stroke-[1.5px] flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-500 stroke-[1.5px] flex-shrink-0" />
                  )}
                  <span className="text-sm text-slate-700 flex-1 truncate">{r.name}</span>
                  {r.isDuplicate && (
                    <span className="text-xs text-amber-600 max-w-[200px] truncate">{r.error}</span>
                  )}
                  {!r.success && !r.isDuplicate && r.error && (
                    <span className="text-xs text-red-500 max-w-[200px] truncate">{r.error}</span>
                  )}
                  <Badge variant="outline" className={`rounded-sm text-[10px] flex-shrink-0 ${
                    r.success ? 'border-green-300 text-green-700' :
                    r.isDuplicate ? 'border-amber-300 text-amber-700' :
                    'border-red-300 text-red-700'
                  }`}>
                    {r.success ? 'Uploaded' : r.isDuplicate ? 'Duplicate' : 'Failed'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
